import { axiosInstance } from "../redux/constants/axiosInstance";
import { API_URL } from "../redux/constants/type";

const BASE_URL = `${API_URL}/api/v2/templates`;
const AVAILABLE_FIELDS_URL = `${API_URL}/api/TemplateBuilder/AvailableFields`;
const EVENTS_URL = `${API_URL}/api/v2/events`;

const unwrap = (response) => {
  const payload = response?.data;
  if (payload?.status === false) {
    throw new Error(payload.message || "Template request failed");
  }
  return payload?.data ?? payload;
};

export const grapesTemplateApi = {
  async list(type) {
    const response = await axiosInstance.get(BASE_URL, {
      params: type ? { type } : undefined,
    });
    return unwrap(response);
  },

  async get(id) {
    const response = await axiosInstance.get(`${BASE_URL}/${id}`);
    return unwrap(response);
  },

  async create(payload) {
    const response = await axiosInstance.post(BASE_URL, payload);
    return unwrap(response);
  },

  async saveDraft(id, payload) {
    const response = id
      ? await axiosInstance.put(`${BASE_URL}/${id}/draft`, payload)
      : await axiosInstance.post(BASE_URL, payload);
    return unwrap(response);
  },

  async publish(id) {
    const response = await axiosInstance.post(`${BASE_URL}/${id}/publish`);
    return unwrap(response);
  },

  async setDefault(id) {
    const response = await axiosInstance.post(`${BASE_URL}/${id}/default`);
    return unwrap(response);
  },

  async duplicate(id) {
    const response = await axiosInstance.post(`${BASE_URL}/${id}/duplicate`);
    return unwrap(response);
  },

  async remove(id) {
    const response = await axiosInstance.delete(`${BASE_URL}/${id}`);
    return unwrap(response);
  },

  async preview(payload) {
    const response = await axiosInstance.post(`${BASE_URL}/preview`, payload);
    return unwrap(response);
  },

  async renderPublished(id, data) {
    const response = await axiosInstance.post(`${BASE_URL}/${id}/render`, {
      data: data || {},
    });
    return unwrap(response);
  },

  async renderInvoice(id, invoiceId) {
    const response = await axiosInstance.post(
      `${BASE_URL}/${id}/documents/invoices/${invoiceId}/render`
    );
    return unwrap(response);
  },

  async renderInvoicePdf(id, invoiceId) {
    return axiosInstance.post(
      `${BASE_URL}/${id}/documents/invoices/${invoiceId}/pdf`,
      {},
      { responseType: "blob" }
    );
  },

  async renderDefaultInvoicePdf(invoiceId) {
    return axiosInstance.post(
      `${BASE_URL}/default/documents/invoices/${invoiceId}/pdf`,
      {},
      { responseType: "blob" }
    );
  },

  async renderProformaPdf(id, performaId) {
    return axiosInstance.post(
      `${BASE_URL}/${id}/documents/proformas/${performaId}/pdf`,
      {},
      { responseType: "blob" }
    );
  },

  async renderDefaultProformaPdf(performaId) {
    return axiosInstance.post(
      `${BASE_URL}/default/documents/proformas/${performaId}/pdf`,
      {},
      { responseType: "blob" }
    );
  },

  async renderQuote(id, quoteId) {
    const response = await axiosInstance.post(
      `${BASE_URL}/${id}/documents/quotes/${quoteId}/render`
    );
    return unwrap(response);
  },

  async renderQuotePdf(id, quoteId) {
    return axiosInstance.post(
      `${BASE_URL}/${id}/documents/quotes/${quoteId}/pdf`,
      {},
      { responseType: "blob" }
    );
  },

  async renderDefaultQuotePdf(quoteId) {
    return axiosInstance.post(
      `${BASE_URL}/default/documents/quotes/${quoteId}/pdf`,
      {},
      { responseType: "blob" }
    );
  },

  async renderPurchaseOrderPdf(id, orderId) {
    return axiosInstance.post(
      `${BASE_URL}/${id}/documents/purchase-orders/${orderId}/pdf`,
      {},
      { responseType: "blob" }
    );
  },

  async renderDefaultPurchaseOrderPdf(orderId) {
    return axiosInstance.post(
      `${BASE_URL}/default/documents/purchase-orders/${orderId}/pdf`,
      {},
      { responseType: "blob" }
    );
  },

  async renderStock(id, filter) {
    const response = await axiosInstance.post(
      `${BASE_URL}/${id}/documents/stock/render`,
      filter || {}
    );
    return unwrap(response);
  },

  async renderStockPdf(id, filter) {
    return axiosInstance.post(
      `${BASE_URL}/${id}/documents/stock/pdf`,
      filter || {},
      { responseType: "blob" }
    );
  },

  async availableFields(templateType, eventId) {
    const response = await axiosInstance.get(AVAILABLE_FIELDS_URL, {
      params:
        templateType || eventId
          ? { templateType, eventId }
          : undefined,
    });
    return unwrap(response);
  },

  // Notification email events are owned by the Event catalog.  The API uses
  // the authenticated company context; callers must not supply a company id.
  async notificationEvents() {
    const response = await axiosInstance.get(EVENTS_URL, {
      params: { channel: "email", active: true },
    });
    return unwrap(response);
  },

  async effectiveNotificationTemplates() {
    const response = await axiosInstance.get(
      `${BASE_URL}/notification-emails/effective`,
    );
    return unwrap(response);
  },

  async createWorkspaceOverride(systemTemplateId) {
    const response = await axiosInstance.post(
      `${BASE_URL}/${systemTemplateId}/workspace-override`,
    );
    return unwrap(response);
  },

  async revertWorkspaceOverride(templateId) {
    const response = await axiosInstance.post(
      `${BASE_URL}/${templateId}/revert-to-system`,
    );
    return unwrap(response);
  },
};

export default grapesTemplateApi;
